import { css, cx } from '@linaria/core'
import type { FC } from 'react'
import { fragments, style } from '#styles/index'

import type {
  AnyComponent,
  AnyComponentType,
  FigureComponent,
  HighlightComponent,
  ImageComponent,
  MetaFigureComponent,
  NoTranslateComponent,
  ParagraphComponent,
  RowComponent,
  SpecialCharacterComponent,
  SubComponent,
  SupComponent,
  TableComponent,
  UnorderedListComponent,
  VCodeLinkComponent,
} from './document'

const Image: FC<{ element: ImageComponent }> = ({ element }) => {
  return <img className="_cosima-image" src={`/cosima/${element.fileName}`} />
}

const Figure: FC<{
  element: FigureComponent[] | MetaFigureComponent[] | FigureComponent | MetaFigureComponent
  className?: string
}> = ({ element, className }) => {
  const figures = Array.isArray(element) ? element : [element]
  // there is a quirk where a meta figure only shows its first image, even when there are more referenced
  const isMeta = !Array.isArray(element) && element.type === 'meta-figure'
  return (
    <div className={cx('_cosima-figure', className)}>
      {isMeta
        ? figures.map((x, i) => {
            if (x.images.length <= 0) {
              return undefined
            }

            return <Image key={i} element={x.images[0]} />
          })
        : figures.map((x, i) => x.images.map((y, j) => <Image key={`${i}--${j}`} element={y} />))}
    </div>
  )
}

const cosimaTableClass = css`
  &._cosima-grid {
    display: grid;

    > ._cosima-header {
      display: contents;

      > ._cosima-row {
        display: contents;
      }

      ${fragments.textStyle.body.l.bold}
      margin-bottom: 12px;
    }

    > ._cosima-body {
      > ._cosima-row {
        display: contents;

        > ._cosima-entry {
          border-bottom: 1px solid ${style.themeColors.lineDefault};
          min-height: 12px;
        }
      }
      display: contents;
      ${fragments.textStyle.body.m.regular}
    }
  }
`

const Table: FC<{ element: TableComponent; className?: string }> = ({ element, className }) => {
  return (
    <div className={cx('_cosima-table', cosimaTableClass, className)}>
      {element.title && <h1 className="_cosima-table-title">{element.title}</h1>}
      {element.content.map((tableContent, i) => {
        const body = [...tableContent.body]
        const header = (tableContent.header ?? [body.shift()]).filter((x): x is RowComponent => !!x)
        const key = `${i}`
        return (
          <div
            key={key}
            className={cx('_cosima-grid', cosimaTableClass)}
            style={{ gridTemplateColumns: `repeat(${tableContent.columnCount}, auto)` }}
          >
            <div className="_cosima-header">
              {header.map((head, j) => (
                <div key={`${i}--${j}`} className={cx('_cosima-row', head.valign)}>
                  {head.entries.map((headEntry, k) => (
                    <div key={`${i}--${j}--${k}`} className="_cosima-entry">
                      {headEntry.content.map((headEntryContent, l) => (
                        <CosimaElement key={`${i}--${j}--${k}--${l}`} element={headEntryContent} />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="_cosima-body">
              {body.map((body, j) => (
                <div key={`${i}--${j}`} className={cx('_cosima-row', body.valign)}>
                  {body.entries.map((bodyEntry, k) => (
                    <div key={`${i}--${j}--${k}`} className="_cosima-entry">
                      {bodyEntry.content.map((bodyEntryContent, l) => (
                        <CosimaElement key={`${i}--${j}--${k}--${l}`} element={bodyEntryContent} />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const UnorderedList: FC<{ element: UnorderedListComponent; className?: string }> = ({ element, className }) => (
  <>
    {element.title && <h1 className="_cosima-list-title">{element.title}</h1>}
    <ul className={cx('_cosima-list', className)}>
      {element.entries.map((x, i) => {
        switch (x.type) {
          case 'li':
            return (
              <li className="_cosima-list-item" key={i}>
                {x.content.map((y, j) => (
                  <Paragraph key={j} element={y} />
                ))}
              </li>
            )
          case 'p': {
            if (x.text.length <= 0) {
              return undefined
            }
            return (
              <li key={i}>
                <Paragraph element={x} />
              </li>
            )
          }
          case 'combined-p': {
            return (
              <li key={i}>
                <Paragraph element={x} />
              </li>
            )
          }
        }
        return undefined
      })}
    </ul>
  </>
)

const Highlight: FC<{ element: HighlightComponent; className?: string }> = ({ element, className }) => {
  return <span className={cx('_cosima-highlight', className)}>{element.content}</span>
}

const VCodeLink: FC<{ element: VCodeLinkComponent; className?: string }> = ({ element, className }) => {
  return <span className={cx('_cosima-vcode-link', className)}>{element.name}</span>
}

const Superscript: FC<{ element: SupComponent; className?: string }> = ({ element, className }) => {
  return (
    <span className={cx('_cosima-superscript', className)}>
      <CosimaElement element={element.content} />
    </span>
  )
}

const Subscript: FC<{ element: SubComponent; className?: string }> = ({ element, className }) => {
  return (
    <span className={cx('_cosima-subscript', className)}>
      <CosimaElement element={element.content} />
    </span>
  )
}

const NoTranslate: FC<{ element: NoTranslateComponent; className?: string }> = ({ element, className }) => {
  return <span className={cx('_cosima-notranslate', element.dir && `_cosima=-${element.dir}`, className)}>{element.content}</span>
}

const SpecialCharacter: FC<{ element: SpecialCharacterComponent; className?: string }> = ({ element, className }) => {
  return <span className={cx('_cosima-special-character', className)}>{element.content}</span>
}

const Paragraph: FC<{ element: ParagraphComponent; className?: string; parentType?: AnyComponentType }> = ({ element, className, parentType }) => {
  if (parentType === 'p' || parentType === 'combined-p') {
    switch (element.type) {
      case 'p':
        return <span className={cx('_cosima-paragraph-inline', className)}>{element.text}</span>
      case 'combined-p':
        return (
          <span className={cx('_cosima-paragraph-inline', className)}>
            {element.components.map((x, i) => {
              return <CosimaElement key={i} element={x} parentType="p" />
            })}
          </span>
        )
    }
  }
  switch (element.type) {
    case 'p':
      return <p className={cx('_cosima-paragraph', className)}>{element.text}</p>
    case 'combined-p':
      return (
        <p className={cx('_cosima-paragraph', className)}>
          {element.components.map((x, i) => {
            return <CosimaElement key={i} element={x} parentType="p" />
          })}
        </p>
      )
  }
}

/**
 * Any element that is likely to be found at any point of rendering can be passed as element.
 * This allows to pass any sub-section of Cosima to be passed as element there-in.
 *
 * For styling, a few rules can be applied. Every element has its own style-able class-names.
 * Elements with a `?` are optional
 * ```txt
 * UnorderedList:   _cosima-list > _cosima-list-item > _cosima-paragraph
 * Table:         ? _cosima-table > _cosima-table-title
 *                ? _cosima-table > _cosima-grid > _cosima-header > _cosima-row > _cosima-entry > _cosima-paragraph
 *                  _cosima-table > _cosima-grid > _cosima-body > _cosima-row > _cosima-entry > _cosima-paragraph
 * Figure:          _cosima-figure > _cosima-image
 * Paragraph:       _cosima-paragraph
 *                ? _cosima-paragraph > _cosima-highlight
 *                ? _cosima-paragraph > _cosima-superscript
 *                ? _cosima-paragraph > _cosima-vcode-link
 *
 * ```
 */
export const CosimaElement: FC<{
  element?: AnyComponent
  className?: string
  parentType?: AnyComponentType
}> = ({ element, className, parentType }) => {
  if (!element) {
    return undefined
  }

  if (typeof element === 'string') {
    return element
  }

  switch (element.type) {
    case 'p':
    case 'combined-p':
      return <Paragraph element={element} className={className} parentType={parentType} />
    case 'table':
      return <Table element={element} className={className} />
    case 'ul':
      return <UnorderedList element={element} className={className} />
    case 'figure':
    case 'meta-figure':
      return <Figure element={element} className={className} />
    case 'highlight':
      return <Highlight element={element} className={className} />
    case 'sup':
      return <Superscript element={element} className={className} />
    case 'sub':
      return <Subscript element={element} className={className} />
    case 'vcode-link':
      return <VCodeLink element={element} className={className} />
    case 'nt':
      return <NoTranslate element={element} className={className} />
    case 'schar':
      return <SpecialCharacter element={element} className={className} />
    case 'unknown':
      return undefined
  }
}
